import React, { useState, useEffect, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
  debounce,
} from "@mui/material";
import {
  DataGridPro,
  GridColDef,
  GridFilterModel,
  GridRenderCellParams,
  GridSortModel,
  getGridStringOperators,
} from "@mui/x-data-grid-pro";
import { axiosWithAuth } from "../plugins/axios";
import { useNavigate } from "react-router-dom";
import { useRole } from "../lib/contexts/RoleContext";
import { getAPIUrlByRole } from "../lib/utils/config";
import DownloadIcon from "@mui/icons-material/Download";
import { StringLiteral } from "typescript";
import saveAs from "file-saver";
import to from "await-to-js";

interface QueueEvaluation {
  assetId: string;
  assetName: string;
  evaluationId: string;
  studentId: string;
  studentName: string;
  cohortId: string;
  programName: string;
  submissionFile: string;
  quizName: string;
  quizId: string;
  submissionFileUrl: string;
  createdAt: string;
  version: string;
}

const EvaluationQueueReport = () => {
  const [queueEvaluations, setQueueEvaluations] = useState<QueueEvaluation[]>(
    []
  );
  const [allQueueEvaluations, setAllQueueEvaluations] = useState<
    QueueEvaluation[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const navigate = useNavigate();
  const role = useRole();
  const apiUrl = getAPIUrlByRole(role);

  useEffect(() => {
    const fetchQueueEvaluations = async () => {
      setError(null);
      try {
        const filters =
          filterModel.items.map((item) => ({
            column: item.field,
            value: item.value,
            operator: item.operator,
          })) || [];

        const response = await axiosWithAuth(
          `${apiUrl}autoGrade/dashboard/queueEvaluations`,
          {
            params: {
              page: page + 1,
              limit: rowsPerPage,
              sortColumn: sortModel[0]?.field || null,
              sortDirection: sortModel[0]?.sort || null,
              filters: JSON.stringify(filters),
            },
          },
          "get"
        );
        setQueueEvaluations(response.data?.data || []);
        // console.log(response?.data?.queueEvaluations)
        setTotalCount(response.data?.totalCount || 0);
        // console.log(response?.data?.totalCount)

        const allResponse = await axiosWithAuth(
          `${apiUrl}autoGrade/dashboard/allQueueEvaluations`,
          {},
          "get"
        );
        setAllQueueEvaluations(allResponse.data?.data || []);
      } catch (err) {
        setError("Failed to load evaluation queues");
      } finally {
        setLoading(false);
      }
    };

    fetchQueueEvaluations();
  }, [apiUrl, page, rowsPerPage, sortModel, filterModel]);

  const debounceFetchQueueEvaluations = useMemo(
    () =>
      debounce((filter) => {
        setFilterModel(filter);
      }, 1000),
    [filterModel]
  );

  const exportEvaluations = () => {
    if (!allQueueEvaluations || allQueueEvaluations.length === 0) {
      console.warn("No data to export.");
      return;
    }

    const isPrism = role === "prism";

    const headers = [
      isPrism ? "Asset ID" : "Quiz ID",
      "Asset Name",
      "Version",
      "Evaluation ID",
      "Student ID",
      "Student Name",
      "Cohort ID",
      "Program Name",
      "Submission File URL",
      "Created At",
    ];

    const csvRows = [
      headers.map((header) => `"${header}"`).join(","),

      ...allQueueEvaluations.map((row) =>
        [
          row.quizId,
          row.quizName,
          row.version,
          row.evaluationId,
          row.studentId,
          row.studentName,
          row.cohortId,
          row.programName,
          row.submissionFileUrl,
          row.createdAt,
        ]
          .map((val) => `"${String(val || "").replace(/"/g, '""')}"`)
          .join(",")
      ),
    ];

    const blob = new Blob([csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    saveAs(blob, "queueEvaluations.csv");
  };


  const downloadUrlCache: Record<string, string> = {};

  const handleDownloadViaAPI = async (url: string) => {
    if (!url) return;

    if (downloadUrlCache[url]) {
      window.open(downloadUrlCache[url], '_blank');
      return;
    }

    try {
      const encodedUrl = encodeURIComponent(url);
      const [err, res] = await to(
        axiosWithAuth(
          `${apiUrl}autograde/downloadFileUpload?url=${encodedUrl}`,
          {},
          'get'
        )
      );

      if (err) {
        throw new Error('Failed to fetch download link');
      }

      const downloadUrl = res.data?.downloadUrl;
      if (!downloadUrl) {
        throw new Error('No download URL returned');
      }

      downloadUrlCache[url] = downloadUrl;
      window.open(downloadUrl, '_blank');
    } catch (error) {
      console.error('Download error:', error);
      setToastMessage((error as Error).message || 'Failed to download file');
      setToastOpen(true);
    }
  };



  const getCustomFilterOperators = () =>
    getGridStringOperators().filter(
      (operator) => operator.value === "contains" || operator.value === "equals"
    );

  const columns: GridColDef[] = [
    {
      field: "quizId",
      headerName: role === "learn" ? "Quiz ID" : "Asset ID",
      width: 160,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.quizId ?? "-"}</>
      ),
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "quizName",
      headerName: "Asset Name",
      width: 300,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.quizName ?? "-"}</>
      ),
      filterOperators: getCustomFilterOperators(),
    },
    ...(role === "prism"
      ? [
        {
          field: "version",
          headerName: "Version",
          width: 150,
          renderCell: (params: GridRenderCellParams) =>
            params.row?.version || "-",
        },
      ]
      : []),
    {
      field: "evaluationId",
      headerName: "Evaluation ID",
      width: 160,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.evaluationId ?? "-"}</>
      ),
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "studentId",
      headerName: "Student ID",
      width: 160,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.studentId ?? "-"}</>
      ),
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "studentName",
      headerName: "Student Name",
      width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.studentName ?? "-"}</>
      ),
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "cohortId",
      headerName: "Cohort ID",
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.cohortId ?? "-"}</>
      ),
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "programName",
      headerName: "Program Name",
      width: 300,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.programName ?? "-"}</>
      ),
      filterOperators: getCustomFilterOperators(),
    },
    // {
    //   field: "submissionFileUrl",
    //   headerName: "Submission File",
    //   width: 100,
    //   align: "center",
    //   headerAlign: "center",
    //   sortable: false,
    //   disableColumnMenu: true,
    //   renderCell: (params: GridRenderCellParams) => (
    //     <Tooltip title="Download Submission File" placement="top">
    //       <IconButton
    //         aria-label="download-submission"
    //         onClick={(e) => {
    //           e.stopPropagation();
    //           window.open(params.row?.submissionFileUrl, "_blank");
    //         }}
    //         sx={{
    //           color: "black",
    //           transition: "transform 0.2s, color 0.2s",
    //           ":hover": {
    //             transform: "scale(1.2)",
    //             color: "primary.main",
    //           },
    //         }}
    //       >
    //         <DownloadIcon />
    //       </IconButton>
    //     </Tooltip>
    //   ),
    // },
    {
      field: "submissionFileUrl",
      headerName: "Submission File",
      width: 100,
      align: "center",
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title="Download Submission File" placement="top">
          <IconButton
            aria-label="download-submission"
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadViaAPI(params.row?.submissionFileUrl);
            }}
            sx={{
              color: "black",
              transition: "transform 0.2s, color 0.2s",
              ":hover": {
                transform: "scale(1.2)",
                color: "primary.main",
              },
            }}
          >
            <DownloadIcon />
          </IconButton>
        </Tooltip>
      ),
    }

  ];

  if (loading) {
    return (
      <Box mt={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom mb={4}>
        Evaluation Queue Report
      </Typography>
      <Box justifyContent={"right"} display={"flex"}>
        <Button
          variant="outlined"
          sx={{ mr: 3 }}
          onClick={exportEvaluations}
          disabled={totalCount <= 0}
        >
          {"Export Dashboard"}
        </Button>
      </Box>

      <Box mt={4}>
        <DataGridPro
          rows={queueEvaluations}
          columns={columns}
          getRowId={(row) => row._id}
          rowHeight={70}
          paginationMode="server"
          sortingMode="server"
          pagination
          paginationModel={{
            page: page,
            pageSize: rowsPerPage,
          }}
          rowCount={totalCount}
          onPaginationModelChange={(newPaginationModel) => {
            setPage(newPaginationModel.page);
            setRowsPerPage(newPaginationModel.pageSize);
          }}
          pageSizeOptions={[5, 10, 25, 50]}
          filterMode="server"
          onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
          onFilterModelChange={(filter) =>
            debounceFetchQueueEvaluations(filter)
          }
        />
      </Box>
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default EvaluationQueueReport;
